import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import Reveal from './Reveal';

/**
 * HomeFAQ
 * Elegant accordion FAQ tuned for the Visual Creationz homepage.
 */

const FAQS = [
  {
    q: 'How many photos and videos can I include?',
    a: 'Each collection includes a set number of photos: up to 20 with the Essential Collection, up to 40 with the Signature Collection and up to 60 with the Cinematic Collection. Need to include more? Just let us know - we can always tailor your project to suit your story.',
  },
  {
    q: 'Can I use my own music?',
    a: 'Absolutely. You can supply your own tracks, request a specific artist or song, or let us hand-pick something emotive from our licensed cinematic library. Your presentation is never forced to end when a song ends - we can edit, extend, repeat or transition tracks where needed to create a seamless finished film. If you supply your own music, please ensure you have the necessary rights or permission to use it.',
  },
  {
    q: 'How long does editing take?',
    a: 'Estimated production times are 1-3 days (Essential), 2-4 days (Signature) and 3-5 days (Cinematic). These begin once all your media has been received. Cinematic takes the longest because it involves the most detailed editing and highest production value.',
  },
  {
    q: 'Can I request changes once the film is delivered?',
    a: 'Yes. Every collection includes free revisions - 1 with the Essential Collection, 2 with the Signature Collection and 3 with the Cinematic Collection. Any remaining balance is payable in line with your selected package and order terms.',
  },
  {
    q: 'How do I upload my files?',
    a: 'After you place your order and pay the booking deposit, you\'ll receive a unique QR code and secure upload link. You can drag and drop files from a laptop, or upload straight from your phone.',
  },
  {
    q: 'Will my film be delivered in 4K?',
    a: 'Yes - every finished film is delivered in cinematic 4K Ultra HD as standard, with a lower-resolution version included for easy sharing.',
  },
  {
    q: 'How will I receive my finished film?',
    a: "Once you're completely happy with the final film, we deliver it two ways: a secure private Google Drive download link (preserving the original 4K quality) plus a physical USB copy, included in every order. Only you and anyone you choose to share the link with can access your presentation, keeping your memories private and secure.",
  },
  {
    q: 'How long will my download be available?',
    a: 'Your private Google Drive download link remains active for 30 days after delivery. We recommend downloading and backing up your presentation as soon as you receive it. Every order also includes a physical USB copy of your finished presentation for safekeeping.',
  },
];

const HomeFAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section className="py-24 bg-gradient-to-b from-black to-gray-900" data-testid="home-faq">
      <div className="container mx-auto px-4 lg:px-8">
        <Reveal className="text-center mb-14 max-w-3xl mx-auto">
          <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Frequently Asked</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Questions? <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">We have answers.</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Everything you need to know before commissioning your cinematic presentation.
          </p>
        </Reveal>

        <div className="max-w-3xl mx-auto space-y-3">
          {FAQS.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <Reveal key={item.q} delay={Math.min(idx + 1, 4)}>
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  data-testid={`faq-item-${idx}`}
                  className={`w-full text-left rounded-2xl border transition-all duration-300 overflow-hidden ${
                    isOpen
                      ? 'border-amber-500/40 bg-white/[0.03]'
                      : 'border-white/10 bg-white/[0.02] hover:border-amber-500/20 hover:bg-white/[0.03]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-6 px-6 py-5">
                    <span className="text-white font-medium text-base md:text-lg leading-snug">
                      {item.q}
                    </span>
                    <span
                      className={`shrink-0 mt-1 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isOpen ? 'bg-amber-500 text-black' : 'bg-white/5 text-gray-300'
                      }`}
                    >
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </div>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-6 text-gray-400 leading-relaxed text-[15px]">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomeFAQ;
